/**
 * src/services/liveSessionPaymentService.ts
 *
 * Executes the Stellar/wallet payment step required to start a paid WebRTC
 * live session.  Mirrors the dual-path signing pattern used elsewhere in
 * GeoTrust (smart-account deposits, profile writes, etc.):
 *
 *   - Passkey smart account (C…)
 *       backend builds + simulates an XLM-SAC transfer from the smart account
 *       to the configured treasury, frontend signs the auth entry with the
 *       passkey, backend adds the service-account fee signature and submits.
 *       → uses POST /api/passkey-kit/prepare-sac-transfer
 *            + POST /api/passkey-kit/fee-pay-and-submit
 *
 *   - Regular Stellar wallet (G…)
 *       frontend builds a classic Payment operation via @stellar/stellar-sdk,
 *       signs with the connected Freighter / WalletConnect wallet, and
 *       submits directly to Horizon.
 *
 * The handler returns { paymentReference, paidAmount } so `profileOverlay` →
 * `liveSessionService.confirmLivePayment` can record the transaction hash on
 * the backend session record.
 */

import {
  Horizon, Networks, TransactionBuilder, Operation, Asset, BASE_FEE,
} from '@stellar/stellar-sdk';
import { passkeyKitService } from './passkeyKitService';
import type { ExecuteLivePayment } from '../components/ProfileOverlay';

// ── Helpers ─────────────────────────────────────────────────────────────────

function stroopsToXlmString(stroops: string | number | bigint): string {
  const s = typeof stroops === 'bigint' ? stroops : BigInt(String(stroops ?? '0'));
  const ZERO = BigInt(0);
  const TEN_M = BigInt('10000000');
  if (s === ZERO) return '0.0000000';
  const neg = s < ZERO;
  const abs = neg ? -s : s;
  const whole = abs / TEN_M;
  const frac  = (abs % TEN_M).toString().padStart(7, '0');
  return `${neg ? '-' : ''}${whole.toString()}.${frac}`;
}

function resolveTreasury(): string {
  const fromEnv = process.env.REACT_APP_LIVE_SESSION_TREASURY;
  if (fromEnv && /^G[A-Z0-9]{55}$/.test(fromEnv)) return fromEnv;
  throw new Error(
    'Live-session treasury address is not configured.  Set ' +
    'REACT_APP_LIVE_SESSION_TREASURY in your .env (G… Stellar account) and reload.',
  );
}

function horizonUrl(): string {
  return (
    process.env.REACT_APP_HORIZON_URL ||
    process.env.REACT_APP_STELLAR_HORIZON_URL ||
    'https://horizon-testnet.stellar.org'
  );
}

function networkPassphrase(): string {
  return process.env.REACT_APP_STELLAR_NETWORK_PASSPHRASE || Networks.TESTNET;
}

function backendUrl(): string {
  return process.env.REACT_APP_BACKEND_URL || '';
}

// ── Smart account (C…) branch ───────────────────────────────────────────────

async function executeViaSmartAccount(opts: {
  smartAccountAddress: string;
  destinationAddress: string;
  amountStroops: string;
}): Promise<{ paymentReference: string; paidAmount?: string }> {
  const { smartAccountAddress, destinationAddress, amountStroops } = opts;
  const record = passkeyKitService.getAccountByWallet(smartAccountAddress)
    || passkeyKitService.getAccounts().find((a) => a.accountAddress === smartAccountAddress)
    || null;
  if (!record) {
    throw new Error('No linked passkey smart account found for this wallet.');
  }

  const prepResp = await fetch(`${backendUrl()}/api/passkey-kit/prepare-sac-transfer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      smartAccountAddress: record.accountAddress,
      destinationAddress,
      amountStroops: Number(amountStroops),
    }),
  });
  if (!prepResp.ok) {
    const err = await prepResp.json().catch(() => ({ error: prepResp.statusText }));
    throw new Error(err.error || `prepare-sac-transfer failed (${prepResp.status})`);
  }
  const prep = await prepResp.json();
  if (!prep.success || !prep.unsignedXdr) {
    throw new Error(prep.error || 'prepare-sac-transfer returned no XDR');
  }

  const signedXdr = await passkeyKitService.signUnsignedXdr(
    prep.unsignedXdr, record, prep.latestLedger,
  );

  const subResp = await fetch(`${backendUrl()}/api/passkey-kit/fee-pay-and-submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ xdrWithPasskeySig: signedXdr }),
  });
  if (!subResp.ok) {
    const err = await subResp.json().catch(() => ({ error: subResp.statusText }));
    throw new Error(err.error || `fee-pay-and-submit failed (${subResp.status})`);
  }
  const sub = await subResp.json();
  if (!sub.success || !sub.hash) {
    throw new Error(sub.error || 'fee-pay-and-submit did not return a hash');
  }

  return { paymentReference: String(sub.hash), paidAmount: String(amountStroops) };
}

// ── Freighter / classic G… branch ───────────────────────────────────────────

export interface GWalletSigner {
  getPublicKey: () => Promise<string>;
  signTransaction: (xdr: string, network?: string) => Promise<string>;
}

async function executeViaGWallet(opts: {
  signer: GWalletSigner;
  destinationAddress: string;
  amountStroops: string;
  memoText?: string;
}): Promise<{ paymentReference: string; paidAmount?: string }> {
  const { signer, destinationAddress, amountStroops, memoText } = opts;

  const net    = networkPassphrase();
  const server = new Horizon.Server(horizonUrl());
  const sender = await signer.getPublicKey();
  const source = await server.loadAccount(sender);

  const amount = stroopsToXlmString(amountStroops);
  let builder = new TransactionBuilder(source, {
    fee: BASE_FEE,
    networkPassphrase: net,
  }).addOperation(Operation.payment({
    destination: destinationAddress,
    asset: Asset.native(),
    amount,
  }));

  if (memoText) {
    const { Memo } = await import('@stellar/stellar-sdk');
    builder = builder.addMemo(Memo.text(memoText.slice(0, 28)));
  }

  const tx = builder.setTimeout(60).build();
  const signedXdr = await signer.signTransaction(tx.toXDR(), net);
  const signed    = TransactionBuilder.fromXDR(signedXdr, net);
  const result    = await server.submitTransaction(signed as any);

  return { paymentReference: String(result.hash), paidAmount: String(amountStroops) };
}

// ── Factory ─────────────────────────────────────────────────────────────────

export interface CreatePaymentHandlerDeps {
  /**
   * The viewer's connected G… wallet signer.  Used directly when the profile
   * is a G… account, and also consulted for fee/sequence on G… accounts.
   */
  gWalletSigner?: GWalletSigner | null;
}

/**
 * Build an `onExecuteLivePayment` handler.  The returned function decides the
 * payment path based on the profile address prefix (C… → passkey, G… →
 * Freighter-like wallet).
 */
export function createLiveSessionPaymentHandler(
  deps: CreatePaymentHandlerDeps,
): ExecuteLivePayment {
  return async (args) => {
    const destinationAddress = resolveTreasury();
    const amountStroops      = String(args.amountStroops || '0');

    if (amountStroops === '0') {
      return { paymentReference: 'zero_cost', paidAmount: '0' };
    }

    // Routing rules (mirror the deposit flow):
    //   1. If the profile address itself is a contract (C…), charge from that
    //      smart account via passkey.
    //   2. Else, if the viewer's G… wallet has a linked passkey smart account,
    //      prefer charging from the smart account (same as the deposit UX).
    //   3. Else, fall back to a classic Freighter/WalletConnect payment from
    //      the G… wallet.

    if (typeof args.wallet === 'string' && args.wallet.startsWith('C')) {
      return executeViaSmartAccount({
        smartAccountAddress: args.wallet,
        destinationAddress,
        amountStroops,
      });
    }

    const linkedSmart = typeof args.wallet === 'string'
      ? passkeyKitService.getAccountByWallet(args.wallet)
      : null;
    if (linkedSmart?.accountAddress) {
      return executeViaSmartAccount({
        smartAccountAddress: linkedSmart.accountAddress,
        destinationAddress,
        amountStroops,
      });
    }

    if (!deps.gWalletSigner) {
      throw new Error('No Stellar wallet is connected to sign the live-session payment.');
    }
    return executeViaGWallet({
      signer: deps.gWalletSigner,
      destinationAddress,
      amountStroops,
      memoText: args.sessionId ? `live:${args.sessionId.slice(0, 20)}` : undefined,
    });
  };
}
